import React from "react";
import "./conditionalStyle.css";

/*
Condition structure

{
  operator: "equals",
  value: "Speaker",
  overrides: [
    { property: "fill", value: "#FFD700" },
    { property: "fontSize", value: "42" }
  ]
}
*/

const propertyDefinitions = {
  fill: { type: "color", label: "Fill" },
  stroke: { type: "color", label: "Stroke" },

  strokeWidth: {
    type: "number",
    label: "Stroke Width",
    min: 0,
    max: 50
  },

  fontSize: {
    type: "number",
    label: "Font Size",
    min: 6,
    max: 200
  },

  opacity: {
    type: "number",
    label: "Opacity",
    min: 0,
    max: 1,
    step: 0.1
  },

  rotation: {
    type: "number",
    label: "Rotation",
    min: -360,
    max: 360
  },

  visible: {
    type: "boolean",
    label: "Visible"
  },

  src: {
    type: "text",
    label: "Image Source"
  }
};

const propertyKeys = Object.keys(propertyDefinitions);

function emptyCondition() {
  return {
    operator: "equals",
    value: "",
    overrides: []
  };
}

function emptyOverride() {
  return {
    property: "fill",
    value: "#000000"
  };
}

export default function ConditionalStyleEditor({ conditions = [], onChange }) {

  function updateCondition(i, patch) {
    const next = [...conditions];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  }

  function removeCondition(i) {
    onChange(conditions.filter((_, idx) => idx !== i));
  }

  function addCondition() {
    onChange([...conditions, emptyCondition()]);
  }

  function updateOverride(ci, oi, patch) {
    const next = [...conditions];
    const overrides = [...(next[ci].overrides || [])];

    overrides[oi] = { ...overrides[oi], ...patch };

    next[ci].overrides = overrides;
    onChange(next);
  }

  function removeOverride(ci, oi) {
    const next = [...conditions];
    const overrides = [...(next[ci].overrides || [])];

    overrides.splice(oi, 1);

    next[ci].overrides = overrides;
    onChange(next);
  }

  function addOverride(ci) {
    const next = [...conditions];
    const overrides = [...(next[ci].overrides || [])];

    overrides.push(emptyOverride());

    next[ci].overrides = overrides;
    onChange(next);
  }

  function renderValueInput(ci, oi, ov) {

    const def = propertyDefinitions[ov.property];

    if (!def) {
      return (
        <input
          type="text"
          value={ov.value || ""}
          onChange={(e) =>
            updateOverride(ci, oi, { value: e.target.value })
          }
        />
      );
    }

    if (def.type === "color") {
      return (
        <input
          type="color"
          value={ov.value || "#000000"}
          onChange={(e) =>
            updateOverride(ci, oi, { value: e.target.value })
          }
        />
      );
    }

    if (def.type === "number") {
      return (
        <input
          type="number"
          min={def.min}
          max={def.max}
          step={def.step || 1}
          value={ov.value || ""}
          onChange={(e) =>
            updateOverride(ci, oi, { value: e.target.value })
          }
        />
      );
    }

    if (def.type === "boolean") {
      return (
        <select
          value={ov.value || "true"}
          onChange={(e) =>
            updateOverride(ci, oi, { value: e.target.value })
          }
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    return (
      <input
        type="text"
        value={ov.value || ""}
        onChange={(e) =>
          updateOverride(ci, oi, { value: e.target.value })
        }
      />
    );
  }

  return (
    <div className="cond-editor">

      {conditions.map((cond, ci) => {

        const usedProps = cond.overrides?.map(o => o.property) || [];

        return (
          <div key={ci} className="cond-card">

            {/* WHEN */}

            <div className="cond-when">

              <span className="cond-label">WHEN</span>

              <select
                value={cond.operator}
                onChange={(e) =>
                  updateCondition(ci, { operator: e.target.value })
                }
              >
                <option value="equals">=</option>
                <option value="notequals">≠</option>
                <option value="contains">contains</option>
                <option value="startswith">starts with</option>
                <option value="endswith">ends with</option>
              </select>

              <input
                type="text"
                placeholder="value"
                value={cond.value || ""}
                onChange={(e) =>
                  updateCondition(ci, { value: e.target.value })
                }
              />

            </div>

            {/* THEN */}

            <div className="cond-then">

              <span className="cond-label">THEN</span>

              {(cond.overrides || []).map((ov, oi) => (

                <div key={oi} className="override-row">

                  <select
                    value={ov.property}
                    onChange={(e) =>
                      updateOverride(ci, oi, {
                        property: e.target.value
                      })
                    }
                  >
                    {propertyKeys
                      .filter(p => p === ov.property || !usedProps.includes(p))
                      .map(p => (
                        <option key={p} value={p}>
                          {propertyDefinitions[p].label}
                        </option>
                      ))}
                  </select>

                  {renderValueInput(ci, oi, ov)}

                  <button
                    className="btn-red"
                    onClick={() => removeOverride(ci, oi)}
                  >
                    X
                  </button>

                </div>

              ))}

              <button
                className="btn-green"
                onClick={() => addOverride(ci)}
              >
                + Property
              </button>



            <button
              className="btn-red"
              onClick={() => removeCondition(ci)}
            >
              Delete Rule
            </button>
          </div>
          </div>
        );
      })}

      <button className="btn-green" onClick={addCondition}>
        + Add Condition
      </button>

    </div>
  );
}
